import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Type, QrCode, Image as ImageIcon,
    Move, Trash2, Settings, Upload, X
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import api from '@services/api';
import toast from 'react-hot-toast';

interface Element {
    id: string;
    type: 'text' | 'qr' | 'image';
    label?: string;
    x: number;
    y: number;
    width?: number; // width in mm
    height?: number; // height in mm
    content?: string;
    placeholder?: string;
    style?: Record<string, any>;
    font_size?: number;
    color?: string;
    align?: 'left' | 'center' | 'right';
}

interface TicketDesignerProps {
    widthMm: number;
    heightMm: number;
    elements: Element[];
    onChange: (elements: Element[]) => void;
    backgroundImage: string | null;
    onBackgroundImageChange: (url: string | null) => void;
}

const MM_TO_PX = 3.7795275591; // 1mm = ~3.78px

export default function TicketDesigner({
    widthMm,
    heightMm,
    elements,
    onChange,
    backgroundImage,
    onBackgroundImageChange,
}: TicketDesignerProps) {
    const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    const [isUploading, setIsUploading] = useState(false);

    // Calculate scaling to fit the canvas within the view
    useEffect(() => {
        const updateScale = () => {
            if (containerRef.current) {
                const parentWidth = containerRef.current.clientWidth;
                const parentHeight = containerRef.current.clientHeight;

                // Available space with padding
                const availableWidth = parentWidth - 40;
                const availableHeight = parentHeight - 40;

                const targetWidth = widthMm * MM_TO_PX;
                const targetHeight = heightMm * MM_TO_PX;

                const scaleX = availableWidth / targetWidth;
                const scaleY = availableHeight / targetHeight;

                // Fit within both width and height or layout breaks
                if (scaleX > 0 && scaleY > 0) {
                    const newScale = Math.min(scaleX, scaleY, 1.2); // Cap zoom at 1.2x
                    setScale(newScale);
                }
            }
        };

        // Initial calculation
        updateScale();

        // Add ResizeObserver
        const resizeObserver = new ResizeObserver(updateScale);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        window.addEventListener('resize', updateScale);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateScale);
        };
    }, [widthMm, heightMm]);

    const handleAddElement = (type: 'text' | 'qr') => {
        const newElement: Element = {
            id: `${type}_${Date.now()}`,
            type,
            x: 10,
            y: 10,
            width: type === 'qr' ? 30 : 100,
            height: type === 'qr' ? 30 : 10,
            content: type === 'text' ? 'New Text' : 'QR Code',
            placeholder: type === 'text' ? '{{ticket.number}}' : '{{ticket.qr_data}}',
            font_size: 12,
            color: '#000000',
            align: 'left',
        };
        onChange([...elements, newElement]);
        setSelectedElementId(newElement.id);
    };

    const handleUpdateElement = (id: string, updates: Partial<Element>) => {
        onChange(elements.map(el => el.id === id ? { ...el, ...updates } : el));
    };

    const handleDeleteElement = (id: string) => {
        onChange(elements.filter(el => el.id !== id));
        setSelectedElementId(null);
    };

    // Image Upload
    const onDrop = async (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'templates');

        try {
            const response = await api.post<{ data: { url: string } }>('/upload/image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            onBackgroundImageChange(response.data.url);
            toast.success('Background image uploaded');
        } catch (error: any) {
            toast.error('Upload failed: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsUploading(false);
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        maxFiles: 1,
    });

    const selectedElement = elements.find(el => el.id === selectedElementId);

    const getBgUrl = (url: string | null) => {
        if (!url) return 'none';
        // Fix for localhost URLs in production
        if (url.includes('localhost') || url.includes('127.0.0.1')) {
            try {
                const urlObj = new URL(url);
                return `url("${urlObj.pathname}")`;
            } catch (e) {
                return `url("${url}")`;
            }
        }
        return `url("${url}")`;
    };

    return (
        <div className="flex flex-col lg:flex-row gap-6 h-full min-h-[600px]">
            {/* Sidebar Tools - Fixed width */}
            <div className="w-full lg:w-72 flex flex-col gap-4 overflow-y-auto pr-2 flex-shrink-0">
                <div className="card p-4 space-y-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white">Tools</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            onClick={() => handleAddElement('text')}
                            type="button"
                            className="flex items-center justify-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                        >
                            <Type className="w-4 h-4" />
                            <span className="text-sm">Add Text</span>
                        </button>
                        <button
                            onClick={() => handleAddElement('qr')}
                            type="button"
                            className="flex items-center justify-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                        >
                            <QrCode className="w-4 h-4" />
                            <span className="text-sm">Add QR</span>
                        </button>
                    </div>

                    <div {...getRootProps()} className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-4 text-center cursor-pointer hover:border-primary-500 transition-colors">
                        <input {...getInputProps()} />
                        {isUploading ? (
                            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                                <span className="animate-spin">⌛</span> Uploading...
                            </div>
                        ) : (
                            <div className="space-y-1">
                                <Upload className="w-8 h-8 mx-auto text-gray-400" />
                                <p className="text-xs text-gray-500">Upload Background</p>
                            </div>
                        )}
                    </div>
                    {backgroundImage && (
                        <button
                            type="button"
                            onClick={() => onBackgroundImageChange(null)}
                            className="w-full text-xs text-danger-600 hover:underline text-center"
                        >
                            Remove Background
                        </button>
                    )}
                </div>

                {/* Selected Element Properties */}
                {selectedElement ? (
                    <div className="card p-4 space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="font-semibold text-gray-900 dark:text-white">Properties</h3>
                            <button
                                type="button"
                                onClick={() => handleDeleteElement(selectedElement.id)}
                                className="text-danger-500 hover:bg-danger-50 p-1 rounded"
                                aria-label="Delete Element"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="label text-xs">Placeholder / Content</label>
                                <input
                                    type="text"
                                    aria-label="Content"
                                    value={selectedElement.placeholder || selectedElement.content}
                                    onChange={(e) => handleUpdateElement(selectedElement.id, { placeholder: e.target.value, content: e.target.value })}
                                    className="input text-xs"
                                />
                                <p className="text-[10px] text-gray-400 mt-1">Use {'{{variable}}'} for dynamic data.</p>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="label text-xs">X (mm)</label>
                                    <input
                                        type="number"
                                        aria-label="X Position"
                                        value={Math.round(selectedElement.x)}
                                        onChange={(e) => handleUpdateElement(selectedElement.id, { x: Number(e.target.value) })}
                                        className="input text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="label text-xs">Y (mm)</label>
                                    <input
                                        type="number"
                                        aria-label="Y Position"
                                        value={Math.round(selectedElement.y)}
                                        onChange={(e) => handleUpdateElement(selectedElement.id, { y: Number(e.target.value) })}
                                        className="input text-xs"
                                    />
                                </div>
                            </div>

                            {selectedElement.type === 'text' && (
                                <>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="label text-xs">Font Size</label>
                                            <input
                                                type="number"
                                                aria-label="Font Size"
                                                value={selectedElement.font_size}
                                                onChange={(e) => handleUpdateElement(selectedElement.id, { font_size: Number(e.target.value) })}
                                                className="input text-xs"
                                            />
                                        </div>
                                        <div>
                                            <label className="label text-xs">Color</label>
                                            <input
                                                type="color"
                                                aria-label="Color"
                                                value={selectedElement.color}
                                                onChange={(e) => handleUpdateElement(selectedElement.id, { color: e.target.value })}
                                                className="h-8 w-full cursor-pointer rounded border border-gray-300"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label text-xs">Alignment</label>
                                        <select
                                            aria-label="Text Alignment"
                                            value={selectedElement.align}
                                            onChange={(e) => handleUpdateElement(selectedElement.id, { align: e.target.value as any })}
                                            className="input text-xs"
                                        >
                                            <option value="left">Left</option>
                                            <option value="center">Center</option>
                                            <option value="right">Right</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {selectedElement.type === 'qr' && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="label text-xs">Size (mm)</label>
                                        <input
                                            type="number"
                                            aria-label="QR Size"
                                            value={selectedElement.width}
                                            onChange={(e) => {
                                                const size = Number(e.target.value);
                                                handleUpdateElement(selectedElement.id, { width: size, height: size });
                                            }}
                                            className="input text-xs"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="card p-4 text-center text-gray-500 text-sm">
                        Select an element to edit properties
                    </div>
                )}
            </div>

            {/* Canvas Area - Flex 1 to take remaining space */}
            <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center p-8 relative" ref={containerRef}>
                <div
                    className="bg-white shadow-2xl relative overflow-hidden transition-all duration-300 ease-in-out origin-center bg-cover bg-center"
                    style={{
                        width: `${widthMm * MM_TO_PX}px`,
                        height: `${heightMm * MM_TO_PX}px`,
                        transform: `scale(${scale})`,
                        backgroundImage: getBgUrl(backgroundImage),
                    }}
                    onClick={() => setSelectedElementId(null)}
                >
                    {/* Grid Pattern (Optional visual aid) */}
                    <div className="absolute inset-0 pointer-events-none opacity-[0.05] ticket-grid-pattern" />

                    {elements.map((el) => (
                        <motion.div
                            key={el.id}
                            drag
                            dragMomentum={false}
                            onDragStart={() => setSelectedElementId(el.id)}
                            onDragEnd={(_, info) => {
                                const deltaXmm = info.offset.x / MM_TO_PX;
                                const deltaYmm = info.offset.y / MM_TO_PX;
                                handleUpdateElement(el.id, {
                                    x: el.x + deltaXmm,
                                    y: el.y + deltaYmm
                                });
                            }}
                            style={{
                                x: 0, // CRITICAL: Reset transform to prevent double-movement
                                y: 0, // CRITICAL: Reset transform to prevent double-movement
                                position: 'absolute',
                                left: `${el.x * MM_TO_PX}px`,
                                top: `${el.y * MM_TO_PX}px`,
                                width: el.width ? `${el.width * MM_TO_PX}px` : 'auto',
                                height: el.height ? `${el.height * MM_TO_PX}px` : 'auto',
                                fontSize: el.font_size ? `${el.font_size}pt` : undefined,
                                color: el.color,
                                textAlign: el.align,
                                cursor: 'move',
                                border: selectedElementId === el.id ? '2px solid #3b82f6' : '1px dashed transparent',
                                zIndex: selectedElementId === el.id ? 10 : 1,
                            }}
                            className="select-none flex items-center"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedElementId(el.id);
                            }}
                        >
                            {el.type === 'qr' ? (
                                <div className="w-full h-full bg-black/10 flex items-center justify-center border border-gray-400">
                                    <QrCode className="w-1/2 h-1/2 opacity-50" />
                                </div>
                            ) : (
                                <span className="w-full whitespace-nowrap overflow-visible">
                                    {el.placeholder || el.content}
                                </span>
                            )}

                            {/* Visual anchor for selection */}
                            {selectedElementId === el.id && (
                                <div className="absolute -top-6 left-0 bg-blue-500 text-white text-[10px] px-1 rounded">
                                    x:{Math.round(el.x)} y:{Math.round(el.y)}
                                </div>
                            )}
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}
